import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useForm from '../../../../lib/hooks/useForm';
import { useNotification } from '../../../../shared/Notification/useNotification';
import { updatePrize, createPrize, destroyPrize } from './data/data';
import prizesSchema from './data/prize.schema';

const usePrizeForm = ({ prizeValues }) => {
  const { showSuccess } = useNotification();
  const { t } = useTranslation('campaigns');
  const queryClient = useQueryClient();

  const { mutate: mutateUpdatePrize } = useMutation(updatePrize, {
    onSuccess: () => {
      showSuccess({
        content: t('prizes.notifications.updated'),
        title: t('prizes.notifications.updateTitle'),
      });
      queryClient.invalidateQueries(['getPrize']);
      queryClient.invalidateQueries(['campaignData', prizeValues.campaignUuid]);
    },
  });

  const { mutate: mutateCreatePrize } = useMutation(createPrize, {
    onSuccess: () => {
      showSuccess({
        content: t('prizes.notifications.created'),
        title: t('prizes.notifications.createTitle'),
      });
      queryClient.invalidateQueries(['getPrize']);
      queryClient.invalidateQueries(['campaignData', prizeValues.campaignUuid]);
    },
  });

  const { mutate: mutateDestroyPrize } = useMutation(destroyPrize, {
    onSuccess: () => {
      showSuccess({
        content: t('prizes.notifications.deleted'),
        title: t('prizes.notifications.deleteTitle'),
      });
      queryClient.invalidateQueries(['getPrize']);
      queryClient.invalidateQueries(['campaignData', prizeValues.campaignUuid]);
    },
  });

  const initialValues = {
    id: prizeValues.id,
    message: prizeValues.message || '',
    prizeDescriptions: prizeValues.prizeDescriptions || [''],
    remainingInfo: prizeValues.remainingInfo || {},
    campaignUuid: prizeValues.campaignUuid || '',
    maxDate: prizeValues.campaignEndDate || '',
    minDate: prizeValues.campaignStartDate || '',
  };

  const onSubmit = (values, { resetForm }) => {
    if (values.id) {
      mutateUpdatePrize(values);
    } else {
      mutateCreatePrize(values);
    }

    resetForm();
  };

  const form = useForm({
    initialValues,
    onSubmit,
    enableReinitialize: true,
    validationSchema: prizesSchema,
  });

  const handleDestroyPrize = () => {
    mutateDestroyPrize(form.values);
  };

  const handleChangePrize = (event, index) => {
    const { prizeDescriptions } = form.values;
    const newPrizes = [...prizeDescriptions];
    newPrizes[index] = event.target.value;
    form.setFieldValue('prizeDescriptions', newPrizes);
  };

  const addPrize = () => {
    const { prizeDescriptions } = form.values;
    form.setFieldValue('prizeDescriptions', [...prizeDescriptions, '']);
  };

  const removePrize = index => {
    const { prizeDescriptions } = form.values;
    const newPrizes = [...prizeDescriptions];
    newPrizes.splice(index, 1);
    form.setFieldValue('prizeDescriptions', newPrizes);
  };

  return {
    ...form,
    handleDestroyPrize,
    handleChangePrize,
    addPrize,
    removePrize,
  };
};

export default usePrizeForm;
